/**
 * Render a generic entity-access call under the specific name it stands in for.
 *
 * `get_entity` and `list_related` replaced nine per-entity tools on the server.
 * That is the right shape for the model — two schemas instead of nine — but it
 * costs the reader: every call in a thread renders as "get_entity", so what the
 * agent is doing is only legible by opening the tool request. The call's own
 * arguments carry the specificity the name lost, so recover it for display.
 *
 * Display only. Nothing here is sent anywhere, and an argument shape this
 * cannot interpret falls back to the real tool name rather than guessing.
 */

/**
 * Roboto ID prefix to the entity kind it denotes.
 *
 * Mirrors `ID_PREFIX_TO_TYPE` in
 * `packages/roboto_service/src/roboto_service/ai/tools/entity_access/types.py`,
 * which in turn mirrors the platform's `NewStyleIdPrefix`. There is no
 * Python->TS codegen, so this is a hand-copy: a prefix added on the server
 * shows here as the generic tool name until it is added, which degrades the
 * label rather than misreporting it.
 *
 * **Known drift, accepted.** This is the third table of the same knowledge,
 * after the backend's `ID_PREFIX_TO_TYPE` and this app's own
 * `PREFIX_TO_RESOURCE_TYPE` in
 * `shared/domain/agentThread/clientTools/collectionResourceIds.ts`. Merging
 * the two web-ui copies was considered and declined: the collection one is
 * typed to the narrower `CollectionResourceType` union and is load-bearing
 * (it decides whether a write is refused), while this one is display-only and
 * covers every entity kind. Deriving one from the other would need a
 * `satisfies` constraint and would couple a correctness path to a cosmetic
 * one. What keeps the drift safe is the fallback contract below: an argument
 * shape this cannot interpret renders the real tool name, never a guess.
 */
const ID_PREFIX_TO_ENTITY: Readonly<Record<string, string>> = {
  cf: "custom_field",
  cl: "collection",
  cm: "comment",
  ds: "dataset",
  ev: "event",
  fl: "file",
  iv: "invocation",
  mp: "message_path",
  mt: "metric_definition",
  og: "org",
  se: "session",
  tp: "topic",
  tr: "trigger",
};

/** Entity kinds identified by an org-scoped natural key rather than a prefix. */
const NATURAL_KEY_ENTITIES: readonly string[] = ["action", "device"];

/**
 * Entity kinds that appear only as the `related_type` of a `list_related` call.
 *
 * Mirrors `LISTING_ONLY_TYPES` in the backend's `entity_access/types.py`. A
 * metric value is what one session recorded for one metric definition;
 * nothing mints an ID for that pair, so it can never be a source, but a
 * session's listing of them still renders as "list_session_metrics" rather
 * than the generic tool name.
 */
const LISTABLE_ONLY_ENTITIES: readonly string[] = ["metric"];

/**
 * Kinds `get_entity` can fetch and `list_related` can start from: every kind
 * with an ID prefix or a natural key.
 */
const SOURCE_ENTITIES: readonly string[] = [
  ...Object.values(ID_PREFIX_TO_ENTITY),
  ...NATURAL_KEY_ENTITIES,
];

/** Kinds `list_related` can name as its `related_type`. */
const RELATED_ENTITIES: readonly string[] = [
  ...SOURCE_ENTITIES,
  ...LISTABLE_ONLY_ENTITIES,
];

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * The entity kind an ID denotes, or `undefined`.
 *
 * The prefix is the whole token before the first underscore, matched exactly:
 * prefixes shadow each other as string prefixes (`tp` vs `tpt`), so
 * `startsWith` would mislabel.
 */
function entityFromId(entityId: string): string | undefined {
  const separator = entityId.indexOf("_");
  if (separator <= 0 || separator === entityId.length - 1) {
    return undefined;
  }
  return ID_PREFIX_TO_ENTITY[entityId.slice(0, separator)];
}

function sourceEntity(input: Record<string, unknown>): string | undefined {
  const declared = asString(input.entity_type);
  if (declared !== undefined) {
    return SOURCE_ENTITIES.includes(declared) ? declared : undefined;
  }
  const entityId = asString(input.entity_id);
  return entityId === undefined ? undefined : entityFromId(entityId);
}

export function describeToolCall(
  toolName: string,
  input: Record<string, unknown> | undefined,
): string {
  const params = input ?? {};

  if (toolName === "get_entity") {
    const entity = sourceEntity(params);
    return entity === undefined ? toolName : `get_${entity}`;
  }

  if (toolName === "list_related") {
    const related = asString(params.related_type);
    if (related === undefined || !RELATED_ENTITIES.includes(related)) {
      return toolName;
    }
    // An omitted entity_id means the caller's own org is the source -- the
    // org-level listing, which had no per-entity tool to be named after.
    const source =
      asString(params.entity_id) === undefined ? "org" : sourceEntity(params);
    return source === undefined ? toolName : `list_${source}_${related}s`;
  }

  return toolName;
}
